import React, {ReactElement, ReactText} from 'react';
import {useIntl} from 'react-intl';
import LayoutSection, {LayoutBackground} from '../../DesignSystem/Layout/LayoutSection/LayoutSection';
import {LayoutCircleConfig, LayoutCircleType} from '../../DesignSystem/Layout/LayoutSection/LayoutSectionCircles';
import BusinessStatsItem from './BusinessStatsItem';
import {
  activeInCountries,
  BusinessStatsItemContent,
  customers,
  nlFunding,
  totalFundingInMillionsWithEuro,
  ukFunding,
} from './stats';
import AnimatedNumber from '../../DesignSystem/AnimatedNumber/AnimatedNumber';

import useFetchBusinessStats from '../../../data/useFetchBusinessStats/useFetchBusinessStats';
import {Locale, useLocale} from '../../../RootProvider/LocaleContext';

import './BusinessStats.scss';
import Paragraph from '../../DesignSystem/Text/Paragraph/Paragraph';

const businessStatsCircles: LayoutCircleConfig = {
  desktop: [
    {
      type: LayoutCircleType.LIGHT,
      style: {
        width: '40rem',
        top: '-20rem',
        left: '-30rem',
      },
    },
    {
      type: LayoutCircleType.LIGHT,
      style: {
        width: '13.8rem',
        top: '3rem',
        right: '-9rem',
      },
    },
  ],
  tablet: [
    {
      type: LayoutCircleType.LIGHT,
      style: {
        width: '40rem',
        top: '-20rem',
        right: '90%',
      },
    },
    {
      type: LayoutCircleType.LIGHT,
      style: {
        width: '13.8rem',
        top: '3rem',
        left: '95%',
      },
    },
  ],
  mobile: [
    {
      type: LayoutCircleType.LIGHT,
      style: {
        width: '26.5rem',
        top: '-13rem',
        left: '-13rem',
      },
    },
  ],
};

function BusinessStats(): ReactElement {
  const {formatMessage} = useIntl();
  const locale = useLocale();

  const {
    stats: {ukFundedAmount, nlFundedAmount, fundedCustomers, totalFunding},
  } = useFetchBusinessStats();

  let contents: BusinessStatsItemContent[] = [
    ukFunding({
      statValue: ukFundedAmount || formatMessage({id: 'homepage.businessStats.ukFunding.stat.value'}),
      statTemplate: (statTemplateValue: ReactText) =>
        formatMessage({id: 'homepage.businessStats.ukFunding.stat.template'}, {stat: statTemplateValue}),
      description: formatMessage({id: 'homepage.businessStats.ukFunding.description'}),
    }),
    customers({
      statValue: fundedCustomers || formatMessage({id: 'homepage.businessStats.customers.stat.value'}),
      statTemplate: (statTemplateValue: ReactText) =>
        formatMessage({id: 'homepage.businessStats.customers.stat.template'}, {stat: statTemplateValue}),
      description: formatMessage({id: 'homepage.businessStats.customers.description'}),
    }),
    nlFunding({
      statValue: nlFundedAmount || formatMessage({id: 'homepage.businessStats.nlFunding.stat.value'}),
      statTemplate: (statTemplateValue: ReactText) =>
        formatMessage({id: 'homepage.businessStats.nlFunding.stat.template'}, {stat: statTemplateValue}),
      description: formatMessage({id: 'homepage.businessStats.nlFunding.description'}),
    }),
  ];

  if ([Locale.nl].includes(locale)) {
    contents = [
      totalFundingInMillionsWithEuro({
        statValue:
          totalFunding || formatMessage({id: 'homepage.businessStats.totalFundingInMillionsWithEuro.stat.value'}),
        statTemplate: (statTemplateValue: ReactText) =>
          formatMessage(
            {id: 'homepage.businessStats.totalFundingInMillionsWithEuro.stat.template'},
            {stat: statTemplateValue}
          ),
        description: formatMessage({id: 'homepage.businessStats.totalFundingInMillionsWithEuro.description'}),
      }),
      customers({
        statValue: fundedCustomers || formatMessage({id: 'homepage.businessStats.customers.stat.value'}),
        statTemplate: (statTemplateValue: ReactText) =>
          formatMessage({id: 'homepage.businessStats.customers.stat.template'}, {stat: statTemplateValue}),
        description: formatMessage({id: 'homepage.businessStats.customers.description'}),
      }),
      activeInCountries({
        title: formatMessage({id: 'homepage.businessStats.activeInCountries.title'}),
        description: formatMessage({id: 'homepage.businessStats.activeInCountries.description'}),
      }),
    ];
  }

  return (
    <LayoutSection innerClassName="businessStats" type={LayoutBackground.ACCENT} circles={businessStatsCircles}>
      {contents.map((content, i) => {
        return (
          <BusinessStatsItem
            Icon={content.Icon}
            headline={(className) => {
              if ('stat' in content && 'statTemplate' in content) {
                if (typeof content.stat === 'string') {
                  // eslint-disable-next-line no-param-reassign
                  content.stat = parseInt(content.stat, 10);
                }

                return <AnimatedNumber className={className} value={content.stat} template={content.statTemplate} />;
              }

              return <Paragraph className={className}>{content.title}</Paragraph>;
            }}
            description={content.description}
            key={i.toString()}
          />
        );
      })}
    </LayoutSection>
  );
}

export default BusinessStats;
